import React, { useRef, useState, useEffect } from "react";
import api from "@/lib/apiClient";
import { FilesetResolver, PoseLandmarker } from "@mediapipe/tasks-vision";
import { Camera, Upload, Loader2, Zap, CheckCircle2, AlertTriangle, Plus, Minus, Activity, Target, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const VERDICT_COLOR = { Excellent: "#FF6B6B", Good: "#4ECCD3", "Needs Work": "#FFB800", Risky: "#FF3B30" };

const PRESETS = {
  squat: { label: "Squats", joints: [[23, 25, 27], [24, 26, 28]], down: 100, up: 155 },
  pushup: { label: "Push-ups", joints: [[11, 13, 15], [12, 14, 16]], down: 95, up: 150 },
  curl: { label: "Bicep Curls", joints: [[11, 13, 15], [12, 14, 16]], down: 55, up: 150 },
};

function angle(a, b, c) {
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const mag = Math.hypot(ab.x, ab.y) * Math.hypot(cb.x, cb.y) || 1e-6;
  return (Math.acos(Math.max(-1, Math.min(1, dot / mag))) * 180) / Math.PI;
}

export default function FormChecker() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const streamRef = useRef(null);
  const [camOn, setCamOn] = useState(false);
  const [exercise, setExercise] = useState("Squat");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [reps, setReps] = useState(0);
  const [tracking, setTracking] = useState(false);
  const [loadingModel, setLoadingModel] = useState(false);
  const [preset, setPreset] = useState("squat");
  const [goal, setGoal] = useState(10);
  const [goalHit, setGoalHit] = useState(false);
  const presetRef = useRef("squat");
  const landmarkerRef = useRef(null);
  const rafRef = useRef(null);
  const repState = useRef("up");
  const lastVideoTime = useRef(-1);

  useEffect(() => () => {
    cancelAnimationFrame(rafRef.current);
    landmarkerRef.current?.close?.();
  }, []);

  const ensureLandmarker = async () => {
    if (landmarkerRef.current) return landmarkerRef.current;
    setLoadingModel(true);
    try {
      const fileset = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm"
      );
      landmarkerRef.current = await PoseLandmarker.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath:
            "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
        },
        runningMode: "VIDEO",
        numPoses: 1,
      });
      return landmarkerRef.current;
    } finally {
      setLoadingModel(false);
    }
  };

  const loop = () => {
    const v = videoRef.current;
    const lm = landmarkerRef.current;
    if (!v || !lm || v.readyState < 2) { rafRef.current = requestAnimationFrame(loop); return; }
    if (v.currentTime !== lastVideoTime.current) {
      lastVideoTime.current = v.currentTime;
      const res = lm.detectForVideo(v, performance.now());
      const pts = res.landmarks?.[0];
      if (pts) {
        const cfg = PRESETS[presetRef.current];
        const a1 = angle(pts[cfg.joints[0][0]], pts[cfg.joints[0][1]], pts[cfg.joints[0][2]]);
        const a2 = angle(pts[cfg.joints[1][0]], pts[cfg.joints[1][1]], pts[cfg.joints[1][2]]);
        const val = Math.min(a1, a2);
        if (val < cfg.down && repState.current === "up") repState.current = "down";
        else if (val > cfg.up && repState.current === "down") {
          repState.current = "up";
          setReps((r) => r + 1);
        }
      }
    }
    rafRef.current = requestAnimationFrame(loop);
  };

  const startTracking = async () => {
    if (!camOn) { await startCam(); }
    try {
      await ensureLandmarker();
      setTracking(true);
      repState.current = "up";
      rafRef.current = requestAnimationFrame(loop);
      toast.success("Live rep tracking on — do your squats!");
    } catch {
      toast.error("Could not load pose model");
    }
  };

  useEffect(() => {
    if (goal > 0 && reps >= goal && !goalHit) {
      setGoalHit(true);
      toast.success(`🎉 Goal smashed — ${reps}/${goal} ${PRESETS[preset].label}!`);
    }
    if (reps < goal && goalHit) setGoalHit(false);
  }, [reps, goal, goalHit, preset]);

  const stopTracking = () => {
    cancelAnimationFrame(rafRef.current);
    setTracking(false);
  };

  const startCam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCamOn(true);
    } catch { toast.error("Camera access denied"); }
  };

  const stopCam = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setCamOn(false);
  };

  const analyzeFrame = async () => {
    let b64;
    const canvas = canvasRef.current;
    if (camOn && videoRef.current) {
      canvas.width = videoRef.current.videoWidth || 640;
      canvas.height = videoRef.current.videoHeight || 480;
      canvas.getContext("2d").drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      b64 = canvas.toDataURL("image/jpeg", 0.8);
    } else {
      return toast.error("Start the camera or upload an image first");
    }
    await runAnalysis(b64);
  };

  const runAnalysis = async (b64) => {
    setAnalyzing(true); setResult(null);
    try {
      const res = await api.post("/ai/form-check", { image_base64: b64, exercise });
      setResult(res.data);
      toast.success("Form analyzed!");
    } catch { toast.error("Analysis failed"); } finally { setAnalyzing(false); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => runAnalysis(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <h1 className="font-display font-bold text-4xl uppercase tracking-tight text-white mb-1">AI Form Checker</h1>
      <p className="text-sm text-[#A0A0A0] mb-6">Capture your movement — get instant AI form scoring & corrective cues.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="relative aspect-video rounded-2xl overflow-hidden border border-[#2A2A2A] bg-black mb-4">
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            {!camOn && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-[#A0A0A0]">
                <Camera className="w-10 h-10 mb-2" />
                <p className="text-sm">Camera preview</p>
              </div>
            )}
            <canvas ref={canvasRef} className="hidden" />
          </div>

          <div className="mb-3">
            <p className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-2">Exercise preset (auto-count joint)</p>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(PRESETS).map(([key, cfg]) => (
                <button
                  key={key}
                  data-testid={`preset-${key}`}
                  onClick={() => { setPreset(key); presetRef.current = key; repState.current = "up"; setExercise(cfg.label); }}
                  className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${preset === key ? "border-[#FF6B6B] bg-[#FF6B6B]/10 text-[#FF6B6B]" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <input data-testid="exercise-input" value={exercise} onChange={(e) => setExercise(e.target.value)} placeholder="Exercise (e.g. Squat)" className="flex-1 bg-[#121212] border border-[#2A2A2A] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]" />
          </div>

          <div className="flex gap-2 flex-wrap">
            {!camOn ? (
              <button data-testid="start-camera-btn" onClick={startCam} className="flex-1 py-3 rounded-xl bg-[#2A2A2A] text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#333333] transition-colors"><Camera className="w-4 h-4" /> Start Camera</button>
            ) : (
              <button data-testid="stop-camera-btn" onClick={stopCam} className="py-3 px-4 rounded-xl bg-[#2A2A2A] text-white text-sm font-semibold hover:bg-[#333333] transition-colors">Stop</button>
            )}
            <button data-testid="analyze-form-btn" onClick={analyzeFrame} disabled={analyzing} className="flex-1 py-3 rounded-xl bg-[#FF6B6B] text-black text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2 hover:brightness-110 transition-all disabled:opacity-50">
              {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</> : <><Zap className="w-4 h-4" /> Analyze Form</>}
            </button>
            <button data-testid="upload-form-btn" onClick={() => fileRef.current?.click()} className="py-3 px-4 rounded-xl bg-[#2A2A2A] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[#333333] transition-colors"><Upload className="w-4 h-4" /></button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
          </div>

          {/* Rep counter */}
          <div className={`mt-4 bg-[#1E1E1E] border rounded-2xl p-4 transition-colors ${goalHit ? "border-[#FF6B6B] ring-2 ring-[#FF6B6B]/40" : "border-[#2A2A2A]"}`} data-testid="rep-counter">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#A0A0A0] flex items-center gap-1">
                  Rep Counter {tracking && <span className="text-[#FF6B6B] flex items-center gap-1"><Activity className="w-3 h-3 animate-pulse" /> live</span>}
                </p>
                <p className="font-display font-black text-4xl text-white flex items-baseline gap-1">
                  {reps}<span className="text-lg text-[#A0A0A0]">/ {goal || "∞"}</span>
                  {goalHit && <PartyPopper className="w-6 h-6 text-[#FF6B6B] ml-1" data-testid="goal-hit-icon" />}
                </p>
              </div>
              <div className="flex gap-2">
                <button data-testid="rep-minus-btn" onClick={() => setReps((r) => Math.max(0, r - 1))} className="w-12 h-12 rounded-xl bg-[#2A2A2A] text-white flex items-center justify-center hover:bg-[#333333] transition-colors"><Minus className="w-5 h-5" /></button>
                <button data-testid="rep-plus-btn" onClick={() => setReps((r) => r + 1)} className="w-12 h-12 rounded-xl bg-[#FF6B6B] text-black flex items-center justify-center hover:brightness-110 transition-all"><Plus className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-3">
              <Target className="w-4 h-4 text-[#4ECCD3] shrink-0" />
              <span className="text-xs text-[#A0A0A0]">Target per set</span>
              <input data-testid="rep-goal-input" type="number" min="0" value={goal} onChange={(e) => setGoal(Math.max(0, Number(e.target.value)))} className="w-20 bg-[#121212] border border-[#2A2A2A] rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#4ECCD3]" />
              <button data-testid="reset-reps-btn" onClick={() => { setReps(0); setGoalHit(false); repState.current = "up"; }} className="ml-auto text-xs px-3 py-1.5 rounded-full bg-[#2A2A2A] text-[#A0A0A0] hover:text-white transition-colors">Reset set</button>
            </div>

            {goalHit && (
              <div data-testid="goal-celebration" className="mt-3 py-2 rounded-xl bg-[#FF6B6B]/10 border border-[#FF6B6B]/30 text-center text-sm font-bold text-[#FF6B6B]">
                🎉 Set complete! Goal of {goal} reps smashed.
              </div>
            )}

            {!tracking ? (
              <button data-testid="start-tracking-btn" onClick={startTracking} disabled={loadingModel} className="mt-3 w-full py-2.5 rounded-xl bg-[#4ECCD3] text-black text-sm font-bold flex items-center justify-center gap-2 hover:brightness-110 transition-all disabled:opacity-50">
                {loadingModel ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading pose model…</> : <><Activity className="w-4 h-4" /> Auto-Count {PRESETS[preset].label}</>}
              </button>
            ) : (
              <button data-testid="stop-tracking-btn" onClick={stopTracking} className="mt-3 w-full py-2.5 rounded-xl bg-[#2A2A2A] text-white text-sm font-semibold hover:bg-[#333333] transition-colors">Stop Auto-Count</button>
            )}
          </div>
        </div>

        {/* Result */}
        <div>
          {!result && !analyzing && (
            <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-10 text-center text-[#A0A0A0] h-full flex flex-col items-center justify-center">
              <Zap className="w-10 h-10 mb-3 text-[#2A2A2A]" />
              <p className="text-sm">Your AI form analysis will appear here.</p>
            </div>
          )}
          {analyzing && <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-10 h-full flex justify-center items-center"><Loader2 className="w-8 h-8 text-[#FF6B6B] animate-spin" /></div>}
          {result && (
            <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-6" data-testid="form-result">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#A0A0A0]">{result.exercise}</p>
                  <p className="font-display font-black text-2xl uppercase" style={{ color: VERDICT_COLOR[result.verdict] || "#FF6B6B" }}>{result.verdict}</p>
                </div>
                <div className="relative w-20 h-20">
                  <svg className="w-20 h-20 -rotate-90"><circle cx="40" cy="40" r="34" stroke="#2A2A2A" strokeWidth="6" fill="none" /><circle cx="40" cy="40" r="34" stroke={VERDICT_COLOR[result.verdict] || "#FF6B6B"} strokeWidth="6" fill="none" strokeDasharray={`${2 * Math.PI * 34}`} strokeDashoffset={`${2 * Math.PI * 34 * (1 - (result.score || 0) / 100)}`} strokeLinecap="round" /></svg>
                  <span className="absolute inset-0 flex items-center justify-center font-display font-black text-xl text-white">{result.score}</span>
                </div>
              </div>

              {result.issues?.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-[#FFB800]" /> Flags</p>
                  <div className="space-y-1.5">
                    {result.issues.map((it, i) => <div key={i} className="flex items-start gap-2 text-sm text-white"><span className="text-[#FFB800] mt-0.5">•</span> {it}</div>)}
                  </div>
                </div>
              )}
              {result.cues?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-2 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-[#FF6B6B]" /> Coaching Cues</p>
                  <div className="space-y-1.5">
                    {result.cues.map((c, i) => <div key={i} className="flex items-start gap-2 text-sm text-white"><span className="text-[#FF6B6B] mt-0.5">✓</span> {c}</div>)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
